import { Currency, isCurrency } from "@redotech/money/iso4217/currencies";
import { moneyFormat } from "@redotech/money/util";
import { Provider } from "@redotech/redo-order-common/provider";
import { RedoRecommendedProduct } from "@redotech/redo-product-common/redo-recommended-product-schema";

/**
 * The currency Shopify already returned these prices in — they're fetched
 * `@inContext`, so the context formatter would double-convert them.
 */
export function resolveRecommendedProductCurrency({
  product,
  selectedVariant,
}: {
  product: RedoRecommendedProduct;
  selectedVariant:
    | RedoRecommendedProduct["variants"]["nodes"][number]
    | undefined;
}): Currency | undefined {
  /**
   * Shopify-only for now: BigCommerce and CommentSold hardcode USD, and SFCC's
   * site-scoped currency is still double-converted (OT follow-up).
   */
  if (product.provider !== Provider.SHOPIFY) {
    return undefined;
  }

  const currencyCode =
    selectedVariant?.price.currencyCode ??
    product.variants.nodes[0]?.price.currencyCode;

  // An unvalidated code would make Intl.NumberFormat throw at render time.
  return currencyCode && isCurrency(currencyCode) ? currencyCode : undefined;
}

/**
 * Formatter for every price on a recommendation card. Prefers the currency
 * Shopify priced the product in; only falls back to the page's context
 * formatter, which applies the shop→presentment rate, when that is unknown.
 */
export function getRecommendedProductPriceFormatter({
  product,
  selectedVariant,
  contextFormatCurrency,
}: {
  product: RedoRecommendedProduct;
  selectedVariant:
    | RedoRecommendedProduct["variants"]["nodes"][number]
    | undefined;
  contextFormatCurrency: (amount: number) => string;
}): (amount: number) => string {
  const currency = resolveRecommendedProductCurrency({
    product,
    selectedVariant,
  });
  return currency
    ? (amount) => moneyFormat({ amount, currency })
    : contextFormatCurrency;
}
